import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';

type BaseBadgeAttributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children'>;

const BadgeVariants = cva(
  `
  inline-flex justify-evenly items-center rounded-lg flex-row flex-nowrap gap-sm
  `,
  {
    variants: {
      size: {
        sm: 'h-5 p-sm text-xs text-nowrap',
        md: 'h-6 p-md text-md text-nowrap',
        lg: 'h-7 p-lg text-lg text-nowrap'
      },
      color: {
        primary: 'bg-background-muted text-primary-base border-2 border-border-base',
        secondary: 'bg-background-muted text-secondary-base border-2 border-border-base', // barely used
        warning: 'bg-warn-bg text-warn-text border-2 border-warn-border',
        error: 'bg-error-bg text-error-text border-2 border-error-border',
        success: 'bg-success-bg text-success-text border-2 border-success-border',
        info: 'bg-info-bg text-info-text border-2 border-info-border',
        neutral: 'bg-background-muted text-foreground-muted border-2 border-border-base'
      },
      outline: {
        primary: 'bg-background-input border-2 border-border-base text-primary-base',
        secondary: 'bg-background-input border-2 border-border-base text-secondary-base', // barely used
        warning: 'bg-background-input text-warn-text border-2 border-warn-text',
        error: 'bg-background-input text-error-text border-2 border-error-text',
        success: 'bg-background-input text-success-text border-2 border-success-text',
        info: 'bg-background-input text-info-text border-2 border-info-border',
        neutral: 'bg-background-input' // TBA
      }
    },
    defaultVariants: {
      size: 'md',
      color: 'primary'
    }
  }
);

type BadgeVariantProps = VariantProps<typeof BadgeVariants>;

export interface BadgeProps extends BaseBadgeAttributes, BadgeVariantProps {
  children?: React.ReactNode;
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, outline, color, children, size, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(BadgeVariants({ size, color, outline }), className)} {...props}>
        {children}
      </div>
    );
  }
);

Badge.displayName = 'Badge';
